/**
 * Datadog RUM and Logs integration for browser telemetry.
 *
 * Provides initialization, user tracking, error reporting, and session replay
 * functionality with proper SSR safety and deferred initialization support.
 */
import { datadogLogs } from '@datadog/browser-logs'
import type { ContextValue, LogsEvent } from '@datadog/browser-logs'
import { datadogRum } from '@datadog/browser-rum'
import type { RumEvent, RumInitConfiguration } from '@datadog/browser-rum'
import Router from 'next/router'

import { API_URL, ENVIRONMENT } from '@/constants/env'

import type { OrganizationTier } from '@/types/BillingOrganization'

// ============================================================================
// Types
// ============================================================================

type DatadogErrorContext = Record<string, unknown>
type AllowedTracingUrls = NonNullable<
  RumInitConfiguration['allowedTracingUrls']
>

export type DatadogUser = {
  id?: string | null
  email?: string | null
  name?: string | null
  firstName?: string | null
  lastName?: string | null
  org?: string | null
  tier?: OrganizationTier | null
}

/** Internal state for Datadog initialization tracking */
type DatadogState = {
  rumInitialized: boolean
  logsInitialized: boolean
  sessionReplayAvailable: boolean
  sessionReplayStarted: boolean
}

declare global {
  interface Window {
    __DATADOG_STATE__?: DatadogState
  }
}

// ============================================================================
// Module State
// ============================================================================

/**
 * Stores the latest user to handle race conditions where setDatadogUser
 * is called before initDatadog completes.
 */
let latestDatadogUser: DatadogUser | null | undefined

// ============================================================================
// State Helpers
// ============================================================================

/**
 * Gets or initializes the Datadog state object.
 * Returns undefined in SSR context.
 */
function getState(): DatadogState | undefined {
  if (typeof window === 'undefined') return undefined
  if (!window.__DATADOG_STATE__) {
    window.__DATADOG_STATE__ = {
      rumInitialized: false,
      logsInitialized: false,
      sessionReplayAvailable: false,
      sessionReplayStarted: false,
    }
  }
  return window.__DATADOG_STATE__
}

/**
 * Checks if code is running in browser context.
 */
function isBrowser(): boolean {
  return typeof window !== 'undefined'
}

// ============================================================================
// Configuration Helpers
// ============================================================================

const DEFAULT_SITE = process.env.NEXT_PUBLIC_DATADOG_SITE ?? 'ddog-gov.com'
const DEFAULT_SERVICE =
  process.env.NEXT_PUBLIC_DATADOG_SERVICE ?? 'sweetspot-www'

const DEFAULT_PRIVACY_LEVEL =
  (process.env.NEXT_PUBLIC_DATADOG_DEFAULT_PRIVACY_LEVEL as
    | 'allow'
    | 'mask'
    | 'mask-user-input'
    | undefined) ?? 'mask-user-input'
const DEFAULT_TRACING_PROPAGATOR_TYPES = ['tracecontext', 'datadog'] as const

function parseSampleRate(value: string | undefined, fallback: number): number {
  if (!value?.trim()) return fallback
  const parsed = Number(value)
  if (!Number.isFinite(parsed)) return fallback
  return Math.min(Math.max(parsed, 0), 100)
}

function getVersion(): string | undefined {
  return (
    process.env.NEXT_PUBLIC_DATADOG_VERSION ??
    process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA ??
    process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_REF ??
    undefined
  )
}

function normalizeTracingOrigin(
  origin: string | undefined,
): string | undefined {
  const trimmed = origin?.trim()
  if (!trimmed) return undefined

  const parseOrigin = (value: string): string | undefined => {
    const url = new URL(value)
    if (
      url.origin === 'null' ||
      (url.protocol !== 'http:' && url.protocol !== 'https:')
    ) {
      return undefined
    }
    if (
      process.env.NODE_ENV !== 'production' &&
      (url.pathname !== '/' || url.search || url.hash)
    ) {
      console.warn(
        'Ignoring path, query, or hash in Datadog tracing origin',
        trimmed,
      )
    }
    return url.origin
  }

  for (const candidate of [trimmed, `https://${trimmed}`]) {
    try {
      const parsed = parseOrigin(candidate)
      if (parsed) return parsed
    } catch {
      // Try the next normalization candidate.
    }
  }

  if (process.env.NODE_ENV !== 'production') {
    console.warn('Ignoring invalid Datadog tracing origin', trimmed)
  }
  return undefined
}

function getAllowedTracingUrls(): AllowedTracingUrls {
  const raw = process.env.NEXT_PUBLIC_DATADOG_ALLOWED_TRACING_ORIGINS
  const configuredOrigins = raw ? raw.split(',') : []
  const origins = [API_URL, ...configuredOrigins]
    .map(normalizeTracingOrigin)
    .filter((origin): origin is string => Boolean(origin))

  return Array.from(new Set(origins)).map((origin) => ({
    match: origin,
    propagatorTypes: [...DEFAULT_TRACING_PROPAGATOR_TYPES],
  }))
}

function resolveEnv(): string {
  if (ENVIRONMENT && ENVIRONMENT.trim().length > 0) {
    return ENVIRONMENT
  }
  return process.env.NODE_ENV ?? 'development'
}

// ============================================================================
// Message Filtering
// ============================================================================

/** Messages that should be filtered from Datadog to reduce noise */
const FILTERED_MESSAGE_PATTERNS = [
  // Next.js navigation cancellation errors
  'Abort fetching component for route',
  'signal is aborted without reason',
]

function shouldFilterDatadogMessage(message: string | undefined): boolean {
  if (!message) return false
  return FILTERED_MESSAGE_PATTERNS.some((pattern) => message.includes(pattern))
}

function redactSsoSetupLink(value: string): string {
  return value.replace(/(\/sso\/setup\/?)[?#][^\s"'<>]*/g, '$1')
}

function redactSsoSetupView(view: { url: string; referrer?: string }): void {
  view.url = redactSsoSetupLink(view.url)
  if (view.referrer) view.referrer = redactSsoSetupLink(view.referrer)
}

/**
 * beforeSend callback for RUM events.
 * Filters out noisy errors (navigation, network errors, etc.).
 */
function rumBeforeSend(event: RumEvent): boolean {
  // A public setup route can follow an already-instrumented client-side route.
  // Keep its capability out of view/referrer events without discarding errors.
  redactSsoSetupView(event.view)
  if (event.type === 'error') {
    event.error.message = redactSsoSetupLink(event.error.message)
    if (event.error.stack) {
      event.error.stack = redactSsoSetupLink(event.error.stack)
    }
    if (event.error.resource) {
      event.error.resource.url = redactSsoSetupLink(event.error.resource.url)
    }
  } else if (event.type === 'resource') {
    event.resource.url = redactSsoSetupLink(event.resource.url)
  } else if (event.type === 'action' && event.action.target) {
    event.action.target.name = redactSsoSetupLink(event.action.target.name)
  }
  if (
    event.type === 'error' &&
    shouldFilterDatadogMessage(event.error.message)
  ) {
    return false
  }
  return true
}

/**
 * beforeSend callback for Log events.
 * Filters out noisy errors (navigation, network errors, etc.).
 */
function logsBeforeSend(log: LogsEvent): boolean {
  redactSsoSetupView(log.view)
  log.message = redactSsoSetupLink(log.message)
  if (log.error?.message) {
    log.error.message = redactSsoSetupLink(log.error.message)
  }
  if (log.error?.stack) log.error.stack = redactSsoSetupLink(log.error.stack)
  if (log.http) log.http.url = redactSsoSetupLink(log.http.url)
  if (
    shouldFilterDatadogMessage(log.message) ||
    shouldFilterDatadogMessage(log.error?.message)
  ) {
    return false
  }
  return true
}

// ============================================================================
// Session Replay
// ============================================================================

function startSessionReplayIfAllowed(): void {
  const state = getState()
  if (!state) return
  if (!state.rumInitialized) return
  if (!state.sessionReplayAvailable) return
  if (state.sessionReplayStarted) return

  datadogRum.startSessionReplayRecording()
  state.sessionReplayStarted = true
}

function stopSessionReplayIfRunning(): void {
  const state = getState()
  if (!state) return
  if (!state.rumInitialized) return
  if (!state.sessionReplayStarted) return

  datadogRum.stopSessionReplayRecording()
  state.sessionReplayStarted = false
}

// ============================================================================
// User Management
// ============================================================================

function applyDatadogUser(user: DatadogUser | null): void {
  const state = getState()
  if (!state?.rumInitialized) return

  if (user && (user.id || user.email)) {
    const { id, email, name, firstName, lastName } = user
    const fullName = [firstName, lastName]
      .filter((value): value is string => Boolean(value && value.trim()))
      .join(' ')
    const displayName = name ?? (fullName.length > 0 ? fullName : undefined)

    startSessionReplayIfAllowed()

    const userPayload = {
      id: id ?? undefined,
      email: email ?? undefined,
      name: displayName,
      firstName: firstName ?? undefined,
      lastName: lastName ?? undefined,
      org: user.org ?? undefined,
      tier: user.tier ?? undefined,
    }

    datadogRum.setUser(userPayload)

    if (state.logsInitialized) {
      datadogLogs.setUser(userPayload)
    }
  } else {
    stopSessionReplayIfRunning()
    datadogRum.clearUser()
    if (state.logsInitialized) {
      datadogLogs.clearUser()
    }
  }
}

// ============================================================================
// Error Normalization
// ============================================================================

/**
 * Normalizes an unknown error value into an Error instance.
 *
 * @param error - The error to normalize (can be Error, string, or object with message)
 * @param fallbackMessage - Message to use if error has no extractable message
 * @returns A proper Error instance
 */
function normalizeError(
  error: unknown,
  fallbackMessage = 'Unknown error',
): Error {
  if (error instanceof Error) return error
  if (typeof error === 'string') return new Error(error)

  // Try to extract message from error-like objects
  const extractedMessage =
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof (error as Record<string, unknown>).message === 'string'
      ? ((error as Record<string, unknown>).message as string)
      : fallbackMessage

  return new Error(extractedMessage)
}

// ============================================================================
// Public API
// ============================================================================

/**
 * Initializes Datadog RUM and Logs.
 *
 * Should be called once on app startup, typically in _app.tsx useEffect.
 * Safe to call multiple times - subsequent calls are no-ops.
 * Safe to call during SSR - returns immediately.
 *
 * Required environment variables:
 * - NEXT_PUBLIC_DATADOG_APPLICATION_ID
 * - NEXT_PUBLIC_DATADOG_CLIENT_TOKEN
 */
export function initDatadog(): void {
  if (!isBrowser()) return

  const state = getState()
  if (!state || (state.rumInitialized && state.logsInitialized)) return

  const applicationId = process.env.NEXT_PUBLIC_DATADOG_APPLICATION_ID
  const clientToken = process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN

  if (!applicationId || !clientToken) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn(
        'Datadog RUM not initialized: missing NEXT_PUBLIC_DATADOG_APPLICATION_ID or NEXT_PUBLIC_DATADOG_CLIENT_TOKEN',
      )
    }
    return
  }

  const sessionSampleRate = parseSampleRate(
    process.env.NEXT_PUBLIC_DATADOG_SESSION_SAMPLE_RATE,
    100,
  )
  const sessionReplaySampleRate = parseSampleRate(
    process.env.NEXT_PUBLIC_DATADOG_SESSION_REPLAY_SAMPLE_RATE,
    100,
  )
  const env = resolveEnv()
  const version = getVersion()
  const usePartitionedCrossSiteSessionCookie =
    process.env.NEXT_PUBLIC_DATADOG_USE_CROSS_SITE_COOKIE === 'true'

  if (!state.rumInitialized) {
    datadogRum.init({
      applicationId,
      clientToken,
      site: DEFAULT_SITE,
      env,
      service: DEFAULT_SERVICE,
      version,
      sessionSampleRate,
      sessionReplaySampleRate,
      defaultPrivacyLevel: DEFAULT_PRIVACY_LEVEL,
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      startSessionReplayRecordingManually: true,
      useSecureSessionCookie: true,
      usePartitionedCrossSiteSessionCookie,
      allowedTracingUrls: getAllowedTracingUrls(),
      // Intentionally propagate trace headers for every matching API request so
      // backend traces can be stitched even when RUM/session sampling changes.
      // This can create APM traces without a sampled RUM session at lower sample rates.
      traceContextInjection: 'all',
      beforeSend: rumBeforeSend,
    })

    state.rumInitialized = true
    state.sessionReplayAvailable = sessionReplaySampleRate > 0
    state.sessionReplayStarted = false

    Router.events.on('routeChangeStart', stopReplayForSsoSetup)
    Router.events.on('hashChangeStart', stopReplayForSsoSetup)
  }

  if (!state.logsInitialized) {
    datadogLogs.init({
      clientToken,
      site: DEFAULT_SITE,
      service: DEFAULT_SERVICE,
      env,
      version,
      useSecureSessionCookie: true,
      usePartitionedCrossSiteSessionCookie,
      forwardErrorsToLogs: true,
      forwardConsoleLogs:
        process.env.NODE_ENV === 'development' ? ['error', 'warn'] : ['error'],
      sessionSampleRate: parseSampleRate(
        process.env.NEXT_PUBLIC_DATADOG_LOGS_SAMPLE_RATE,
        100,
      ),
      beforeSend: logsBeforeSend,
    })

    state.logsInitialized = true
  }

  // Apply any user that was set before initialization completed
  if (latestDatadogUser !== undefined) {
    applyDatadogUser(latestDatadogUser)
  }
}

/**
 * Sets the current user for Datadog tracking.
 *
 * Can be called before initDatadog - the user will be applied once
 * initialization completes. Pass null to clear the user.
 *
 * @param user - User data to set, or null to clear
 */
export function setDatadogUser(user: DatadogUser | null): void {
  if (!isBrowser()) return
  latestDatadogUser = user

  const state = getState()
  if (state?.rumInitialized) {
    applyDatadogUser(user)
  }
}

/** Stop replay before Next writes a setup capability into the browser URL. */
function stopReplayForSsoSetup(url: string): void {
  if (/^\/sso\/setup\/?(?:[?#]|$)/.test(url)) {
    setDatadogUser(null)
  }
}

export const _resetForTesting =
  process.env.NODE_ENV !== 'production'
    ? function _resetForTestingImpl(): void {
        Router.events.off('routeChangeStart', stopReplayForSsoSetup)
        Router.events.off('hashChangeStart', stopReplayForSsoSetup)
        latestDatadogUser = undefined
        if (isBrowser()) {
          delete window.__DATADOG_STATE__
        }
      }
    : undefined

function toDatadogContextValue(
  value: unknown,
  seen = new WeakSet<object>(),
): ContextValue {
  if (
    value === undefined ||
    value === null ||
    ['string', 'number', 'boolean'].includes(typeof value)
  ) {
    return value as ContextValue
  }
  if (Array.isArray(value)) {
    if (seen.has(value)) return '[Circular]'
    seen.add(value)
    try {
      return value.map((entryValue) => toDatadogContextValue(entryValue, seen))
    } finally {
      seen.delete(value)
    }
  }
  if (typeof value === 'object') {
    if (seen.has(value)) return '[Circular]'
    seen.add(value)
    try {
      return Object.fromEntries(
        Object.entries(value).map(([entryKey, entryValue]) => [
          entryKey,
          toDatadogContextValue(entryValue, seen),
        ]),
      )
    } finally {
      seen.delete(value)
    }
  }
  return String(value)
}

/**
 * Adds a global context property to all RUM and Log events.
 *
 * @param key - Context property key
 * @param value - Context property value
 */
export function addDatadogGlobalContext(key: string, value: unknown): void {
  const state = getState()
  if (!state?.rumInitialized) return

  datadogRum.setGlobalContextProperty(key, value)
  if (state.logsInitialized) {
    datadogLogs.setGlobalContextProperty(key, toDatadogContextValue(value))
  }
}

/**
 * Removes a global context property from RUM and Log events.
 *
 * @param key - Context property key to remove
 */
export function clearDatadogGlobalContext(key: string): void {
  const state = getState()
  if (!state?.rumInitialized) return

  datadogRum.removeGlobalContextProperty(key)
  if (state.logsInitialized) {
    datadogLogs.removeGlobalContextProperty(key)
  }
}

/**
 * Reports an error to Datadog RUM and Logs.
 *
 * Safe to call even if Datadog is not initialized - errors will be
 * logged to console in development mode.
 *
 * @param error - The error to report (can be Error, string, or object)
 * @param context - Additional context to attach to the error
 * @param message - Optional message override (defaults to error message)
 */
export function reportDatadogError(
  error: unknown,
  context?: DatadogErrorContext,
  message?: string,
): void {
  if (!isBrowser()) return
  const normalized = normalizeError(error, message)
  const state = getState()

  try {
    if (state?.rumInitialized) {
      datadogRum.addError(normalized, context)
    }
  } catch (err) {
    if (process.env.NODE_ENV !== 'production') {
      console.error('Failed to send error to Datadog RUM', err)
    }
  }

  try {
    if (state?.logsInitialized) {
      datadogLogs.logger.error(message ?? normalized.message, {
        ...context,
        errorName: normalized.name,
        stack: normalized.stack,
      })
    }
  } catch (err) {
    if (process.env.NODE_ENV !== 'production') {
      console.error('Failed to send error to Datadog Logs', err)
    }
  }

  if (process.env.NODE_ENV !== 'production') {
    console.error(normalized)
  }
}

/**
 * Adds a custom action/breadcrumb to the RUM session.
 *
 * Useful for tracking user actions and navigation for debugging.
 *
 * @param message - Description of the action
 * @param context - Additional context for the action
 */
export function addDatadogBreadcrumb(
  message: string,
  context?: Record<string, unknown>,
): void {
  const state = getState()
  if (!state?.rumInitialized) return

  datadogRum.addAction(message, context)
}

/**
 * Reports a chat lifecycle event (failure/cancellation/timeout) to Datadog.
 *
 * Emits a RUM custom action and a browser-log entry (status `error` for
 * failures, `warn` for cancellations) with structured attributes.
 * No-ops silently when Datadog is not initialized.
 */
export function reportChatLifecycleEvent(
  event: string,
  attributes: {
    sessionId?: string | null
    runId?: string | null
    errorCode?: string | null
    errorMsg?: string | null
    source: string
  },
  status: 'error' | 'warn' = 'error',
): void {
  if (!isBrowser()) return
  const state = getState()
  const context = { event, ...attributes }

  try {
    if (state?.rumInitialized) {
      datadogRum.addAction(event, context)
    }
  } catch {
    // no-op: telemetry must never break the app
  }

  try {
    if (state?.logsInitialized) {
      datadogLogs.logger[status](event, context)
    }
  } catch {
    // no-op: telemetry must never break the app
  }
}
